import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Button, CircularProgress } from '@mui/material';
import DrawerCDS, { DrawerSize } from '@sharedCR/coreDesignSystem/Drawer/DrawerCDS';
import { TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { showMessageAction } from 'store/global/actions';
import {
  getLoanBrokerConditionByApplicationIdAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
  rejectLoanConditionAction,
} from 'store/loanApplications/actions';

import { ConditionsTestIds } from '../../Conditions.testIds';

import {
  REJECT_CONDITION_FORM_ID,
  REJECTION_REASON_MAX_LENGTH,
} from './RejectConditionDrawer.constants';
import { formStyles, submitButtonContentStyles } from './RejectConditionDrawer.styles';

const RejectConditionDrawer = ({ open, condition, isBrokerCompany, loanId, onClose }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const trimmedReason = reason.trim();
  const isSubmitDisabled = !trimmedReason || isSaving;
  const drawerTitle = condition.canonicalTitle
    ? `Reject Condition · ${condition.canonicalTitle}`
    : `Reject Condition #${condition.number}`;

  const onReasonChange = useCallback(event => {
    setReason(event.target.value);
  }, []);

  const refreshConditions = useCallback(() => {
    const fetchConditions = isBrokerCompany
      ? getLoanBrokerConditionByApplicationIdAction({ applicationId })
      : getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true });

    return Promise.all([dispatch(fetchConditions), dispatch(getLoanApplicationTasksAction())]);
  }, [applicationId, dispatch, isBrokerCompany, loanId]);

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      if (isSubmitDisabled) {
        return;
      }

      setIsSaving(true);

      dispatch(rejectLoanConditionAction({ loanConditionId: condition.id, reason: trimmedReason }))
        .then(refreshConditions)
        .then(() =>
          dispatch(
            showMessageAction({
              message: `Condition #${condition.number} rejected.`,
              variant: 'success',
            }),
          ),
        )
        .then(onClose)
        .finally(() => setIsSaving(false));
    },
    [
      condition.id,
      condition.number,
      dispatch,
      isSubmitDisabled,
      onClose,
      refreshConditions,
      trimmedReason,
    ],
  );

  const content = (
    <Box
      noValidate
      component="form"
      id={REJECT_CONDITION_FORM_ID}
      sx={formStyles}
      onSubmit={onSubmit}
    >
      <TextFieldCDS
        autoFocus
        fullWidth
        multiline
        required
        dataTestId={ConditionsTestIds.REJECT_REASON_INPUT}
        disabled={isSaving}
        label="Rejection Reason"
        maxLength={REJECTION_REASON_MAX_LENGTH}
        name="reason"
        rows={6}
        value={reason}
        onChange={onReasonChange}
      />
    </Box>
  );

  const actions = (
    <Button
      data-testid={ConditionsTestIds.REJECT_SUBMIT_BUTTON}
      disabled={isSubmitDisabled}
      form={REJECT_CONDITION_FORM_ID}
      type="submit"
      variant="contained"
    >
      <Box sx={submitButtonContentStyles}>
        {isSaving && <CircularProgress color="inherit" size={16} />}
        Reject
      </Box>
    </Button>
  );

  return (
    <DrawerCDS
      actions={actions}
      content={content}
      data-testid={ConditionsTestIds.REJECT_DRAWER}
      isDirty={!!trimmedReason}
      isLoading={isSaving}
      open={open}
      size={DrawerSize.SMALL}
      title={drawerTitle}
      onClose={onClose}
    />
  );
};

RejectConditionDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  condition: PropTypes.object.isRequired,
  isBrokerCompany: PropTypes.bool.isRequired,
  loanId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(RejectConditionDrawer);
